import Avatar from "@material-ui/core/Avatar/Avatar";
import Chip from "@material-ui/core/Chip/Chip";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import Paper from "@material-ui/core/Paper/Paper";
import TextField from "@material-ui/core/TextField/TextField";
import Typography from "@material-ui/core/Typography/Typography";
import withStyles from "@material-ui/core/styles/withStyles";
import Add from "@material-ui/icons/Add";
import Person from "@material-ui/icons/Person";
import Downshift from 'downshift';
import debounce from "lodash.debounce";
import * as React from "react";
import { PersonModel } from "../../model/autogen/PersonModel";
import { PersonInfo } from "model/autogen/PersonInfo";
// import { QuotedSubjectModel } from "model/autogen/QuotedSubjectModel";

function isNew(person: PersonModel) {
    return person.id == 0;
}

function getAvatar(person: PersonModel): JSX.Element {
    return <Avatar>
        {isNew(person) ? <Add /> : <Person />}
    </Avatar>;
}

function renderInput(inputProps: any) {
    const { InputProps, classes, ref, ...other } = inputProps;

    return (
        <TextField
        InputProps={{
            inputRef: ref,
            classes: {
                root: classes.inputRoot
            },
            ...InputProps
        }}
        {...other}
        />
    );
}

interface IRenderSuggestionOptions {
    highlightedIndex: number;
    index: number;
    itemProps: any;
    selectedItem: string;
    suggestion: PersonModel;
}

export function personToString(person: PersonInfo) {
    return person ? `${person.firstName} ${person.lastName}` : "";
}

function renderSuggestion({ suggestion, index, itemProps, highlightedIndex, selectedItem }:
IRenderSuggestionOptions) {
    const isHighlighted = highlightedIndex === index;
    const isSelected = (selectedItem || '').indexOf(suggestion.firstName) > -1 || (selectedItem || '').indexOf(suggestion.lastName) > -1;

    return (
        <MenuItem
            {...itemProps}
            key={suggestion.id}
            selected={isHighlighted}
            component="div"
            style={{
                fontWeight: isSelected ? 500 : 400
            }}>
            {isNew(suggestion) ? <><Add /> {`Add: "${suggestion.firstName} ${suggestion.lastName}"`}</> : <><Person /> {`${suggestion.firstName} ${suggestion.lastName}`}</>}
        </MenuItem>
    );
}

interface IDownshiftMultipleProps {
    initialPeopleIds: number[];
    onFetchPeople: (query: string) => Promise<PersonModel[]>;
    onResolvePeople: (peopleIds: number[]) => Promise<PersonModel[]>;
    onPeopleChanged: (list: PersonModel[]) => void;
    classes: any;
    placeholder: string;
    label: string;
    tweet: boolean;
}

interface IDownshifMultipleState {
    isDebouncing: boolean,
    inputValue: string,
    selectedItem: PersonModel[];
    suggestions: PersonModel[];
}

class DownshiftMultiple extends React.Component<IDownshiftMultipleProps, IDownshifMultipleState> {
    constructor(props: IDownshiftMultipleProps) {
        super(props)
        this.state = {
            isDebouncing: false,
            inputValue: '',
            selectedItem: [],
            suggestions: []
        };
    }

    async componentDidMount() {
        if (this.props.onResolvePeople) {
            const currentPeople = [...this.state.selectedItem];
            const resolved = await this.props.onResolvePeople(this.props.initialPeopleIds);
            this.setState({
                selectedItem: [...resolved, ...currentPeople]
            });
        }
    }

    private debouncedSearch = debounce(async (search: string) => {
        const people = await this.props.onFetchPeople(search);
        this.setState({
            suggestions: people,
            isDebouncing: false
        });
    }, 500);

    private getSuggestions(inputValue: string) {
        //if (this.state.selectedItem.filter(si => si.firstName == inputValue || si.lastName == inputValue).length > 0) {
        //    return [];
        //}
        let count = 0;
        const items = this.state.suggestions.filter(suggestion => {
            let keep;
            if (this.props.tweet) {
                keep = (this.state.selectedItem.filter(si => si.id == suggestion.id).length == 0) && count < 5 && suggestion.twitterHandles.length > 0;
            } else {
                keep = (this.state.selectedItem.filter(si => si.id == suggestion.id).length == 0) && count < 5;
            }
            
            if (keep) {
                count += 1;
            }

            return keep;
        });
        return items;
    };

    private handleKeyDown = (event: React.KeyboardEvent<any>) => {
        const { inputValue, selectedItem } = this.state;
        if (selectedItem.length && !inputValue.length && event.key === 'backspace') {
            const newItems = [...selectedItem.slice(0, selectedItem.length -1)];
            this.setState({
                selectedItem: newItems
            }, () => {
                this.props.onPeopleChanged(newItems);
            });
        }
    };

    private handleInputChange = (event: any) => {
        this.setState({inputValue: event.target.value, isDebouncing: true});
        this.debouncedSearch(event.target.value);
    }

    private handleChange = (item: any) => {
        let { selectedItem } = this.state;
        let broadcast = false;
        if (selectedItem.indexOf(item) === -1) {
            selectedItem = [...selectedItem, item];
            broadcast = true;
        }

        this.setState({
            inputValue: '',
            selectedItem
        }, () => {
            if (broadcast) {
                this.props.onPeopleChanged(selectedItem);
            }
        });
    };

    private handleDelete = (item: any) => () => {
        const selectedItem = [...this.state.selectedItem];
        selectedItem.splice(selectedItem.indexOf(item), 1);
        this.setState({ selectedItem });
        this.props.onPeopleChanged(selectedItem);
    };

    render() {
        const { classes, label } = this.props;
        const { inputValue, selectedItem, isDebouncing } = this.state;

        return (
            <Downshift inputValue={inputValue} onChange={this.handleChange} selectedItem={selectedItem} itemToString={personToString}>
                {({
                    getInputProps,
                    getItemProps,
                    isOpen,
                    inputValue: inputValue2,
                    selectedItem: selectedItem2,
                    highlightedIndex
                }: any) => (
                    <div className={classes.container}>
                        {renderInput ({
                            fullWidth: true,
                            classes,
                            InputProps: getInputProps({
                                startAdornment: selectedItem.map(item => (
                                    <Chip
                                        key={item.id}
                                        avatar={getAvatar(item)}
                                        tabIndex={-1}
                                        label={`${item.firstName} ${item.lastName}`}
                                        className={classes.chip}
                                        onDelete={this.handleDelete(item)}
                                    />
                                )),
                                onChange: this.handleInputChange,
                                onKeyDown: this.handleKeyDown,
                                placeholder: this.props.placeholder,
                                id: 'integration-downshif-multiple',
                            }),
                            label: label
                        })}
                        {isOpen ? (isDebouncing ? (
                            <Paper className={classes.paper} square>
                                <Typography><CircularProgress size={24} /> Finding suggestions ...</Typography>
                            </Paper>
                        ) : (
                            <Paper className={classes.paper} square>
                                {this.getSuggestions(inputValue2).map((suggestion, index) => 
                                    renderSuggestion({
                                        suggestion,
                                        index, 
                                        itemProps: getItemProps({item: suggestion}),
                                        highlightedIndex,
                                        selectedItem: selectedItem2
                                    }),
                                )}
                            </Paper>
                        )) : null}
                    </div>
                )}
            </Downshift>
        );
    }
}

const styles = (theme: any) => ({
    root: {
        marginTop: "10px",
        width: "100%"
    },
    container: {
        position: 'relative',
    },
    paper: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing.unit,
        left: 0,
        right: 0,
    },
    chip: {
        margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
    },
    inputRoot: {
        flexWrap: 'wrap',
    },
});

export interface IPersonListEditorProps {
    initialPeopleIds: number[];
    onFetchPeople: (query: string) => Promise<PersonModel[]>;
    onResolvePeople: (people: number[]) => Promise<PersonModel[]>;
    onPeopleChanged: (list: PersonInfo[] | PersonModel[]) => void;
    classes: any;
    label: string;
    tweet: boolean;
    
}

function PersonListEditor(props: IPersonListEditorProps) {
    const { classes, onPeopleChanged, onFetchPeople, onResolvePeople, initialPeopleIds, label, tweet } = props  
    return (
        <div className={classes.root}>
            <DownshiftMultiple tweet={tweet} label={label} initialPeopleIds={initialPeopleIds} classes={classes} onResolvePeople={onResolvePeople} onPeopleChanged={onPeopleChanged} onFetchPeople={onFetchPeople} placeholder="Select people" />
        </div>
    )  ;
}

export default withStyles(styles as any)(PersonListEditor)


// WEBPACK FOOTER //
// ./src/components/material/person-list-editor.tsx